import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toDateKey } from "@mmm/shared";
import type { EventDto } from "@mmm/contracts";
import { useQuery } from "../../api/useQuery.js";
import { getUpcomingEvents } from "../../api/queries.js";
import { createEvent, updateEvent, deleteEvent } from "../../api/commands.js";
import { MonthGrid } from "../../components/calendar/MonthGrid.js";
import { EventTypeLegend } from "../../components/calendar/EventTypeLegend.js";
import { EventForm, type EventFormValues } from "../../components/calendar/EventForm.js";
import { PageTitle } from "../../components/ui/PageTitle.js";
import { LoadingCalendar, QueryStatus } from "../../components/ui/LoadingPlaceholders.js";
import { ErrorState, useT } from "@mmm/ui";
export function GlobalCalendarScreen() {
  const t = useT();
  const navigate = useNavigate();
  const today = new Date();
  const [monthDate, setMonthDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  // Only subject-less events are editable here; subject events are managed on
  // their subject's calendar.
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const monthStart = toDateKey(new Date(monthDate.getFullYear(), monthDate.getMonth(), 1));
  const monthEnd = toDateKey(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0));

  const events = useQuery(getUpcomingEvents, { from: monthStart, to: monthEnd });
  const visibleEvents = useMemo(() => events.data || [], [events.data]);
  const selectedEvent = visibleEvents.find((e) => e.id === selectedEventId && e.subjectId === null) || null;

  const eventsByDate = useMemo(() => {
    const map = new Map<string, EventDto[]>();
    visibleEvents.forEach((event) => {
      const list = map.get(event.startDate) || [];
      list.push(event);
      map.set(event.startDate, list);
    });
    return map;
  }, [visibleEvents]);

  const handleEventClick = (event: EventDto) => {
    // Subject events open on their subject's calendar; subject-less ones edit here.
    if (event.subjectId) navigate(`/subjects/${event.subjectId}/calendar`);
    else setSelectedEventId(event.id);
  };

  const handleSave = async (values: EventFormValues) => {
    // EventForm's own onSubmit awaits and catches this — a failure here
    // stops before setSelectedEventId, so the form stays showing the event
    // being edited instead of wrongly clearing the selection.
    if (selectedEvent) {
      await updateEvent({ eventId: selectedEvent.id, ...values });
    } else {
      await createEvent(values);
    }
    setSelectedEventId(null);
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    // EventForm's Delete button invokes this without awaiting it — a failure
    // is already toasted by commands.ts's run(); catch it here so it doesn't
    // escape unhandled, and keep the selection so the form doesn't clear.
    try {
      await deleteEvent({ eventId: selectedEvent.id });
      setSelectedEventId(null);
    } catch {
      // Nothing left to do.
    }
  };

  return (
    <div>
      <PageTitle title={t("calendar.title")} />
      <QueryStatus queries={[events]} />

      <div className="mmm-calendar-page mmm-page-section">
        <EventForm
          key={selectedEventId || "new"}
          selectedEvent={selectedEvent}
          onSave={handleSave}
          onDelete={handleDelete}
          onClear={() => setSelectedEventId(null)}
        />
        {events.error && !events.waiting ? (
          <ErrorState title={t("calendar.loadFailed")} onRetry={events.retry} />
        ) : events.loading ? (
          <LoadingCalendar />
        ) : (
          <MonthGrid
            year={monthDate.getFullYear()}
            month={monthDate.getMonth()}
            onMonthChange={(amount) => setMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + amount, 1))}
            eventsByDate={eventsByDate}
            onEventClick={handleEventClick}
          />
        )}
        <div className="mmm-calendar-legend-bar">
          <EventTypeLegend />
        </div>
      </div>
    </div>
  );
}
